import React from 'react'

// The product's icons (docs/25-design-system.md): one small inline-SVG set,
// drawn on a 24px grid with a 2px round stroke in the current text colour,
// shared by her app and the booking site. They replace text glyphs (‹ › ⌄ ×
// + ✓ ↑ ↓ ⋯), which sat off-centre and changed size with the font.
//
// Icons are decorative by default (aria-hidden): the control around one names
// it. Pass `label` only when the icon alone carries meaning.

const PATHS = {
  'chevron-left': <path d="M15 5l-7 7 7 7" />,
  'chevron-right': <path d="M9 5l7 7-7 7" />,
  'chevron-down': <path d="M5 9l7 7 7-7" />,
  'chevron-up': <path d="M5 15l7-7 7 7" />,
  close: <path d="M6 6l12 12M18 6L6 18" />,
  // Thumbs for the AI feedback row (docs/superpowers/specs/2026-10-18-ai-feedback-loop-design.md).
  'thumb-up': <path d="M7 11v8a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1h3a4 4 0 0 0 4-4V6a2 2 0 0 1 4 0v5h3a2 2 0 0 1 2 2l-1 5a2 3 0 0 1-2 2h-7a3 3 0 0 1-3-3" />,
  'thumb-down': <path d="M7 13V5a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h3a4 4 0 0 1 4 4v1a2 2 0 0 0 4 0v-5h3a2 2 0 0 0 2-2l-1-5a2 3 0 0 0-2-2h-7a3 3 0 0 0-3 3" />,
  plus: <path d="M12 5v14M5 12h14" />,
  minus: <path d="M5 12h14" />,
  check: <path d="M5 12.5l4.5 4.5L19 7.5" />,
  'arrow-up': <path d="M12 19V5M6 11l6-6 6 6" />,
  'arrow-down': <path d="M12 5v14M6 13l6 6 6-6" />,
  edit: <path d="M4 20h4L18.5 9.5a2.1 2.1 0 0 0-4-4L4 16z M13.5 6.5l4 4" />,
  // A padlock, on the Pro locks.
  lock: (
    <>
      <rect x="5" y="10.5" width="14" height="10" rx="2.5" />
      <path d="M8.5 10.5V7.5a3.5 3.5 0 0 1 7 0v3" />
    </>
  ),
  star: <path d="M12 3.5l2.6 5.3 5.9.9-4.25 4.1 1 5.8L12 16.9l-5.25 2.75 1-5.8L3.5 9.7l5.9-.9z" />,
  today: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.3 5.3l1.4 1.4M17.3 17.3l1.4 1.4M5.3 18.7l1.4-1.4M17.3 6.7l1.4-1.4" />
    </>
  ),
  calendar: (
    <>
      <rect x="3.5" y="5" width="17" height="15.5" rx="3" />
      <path d="M3.5 10h17M8 3v4M16 3v4" />
    </>
  ),
  clients: (
    <>
      <circle cx="9" cy="8.5" r="3.5" />
      <path d="M2.5 20c.6-3.6 3.2-5.5 6.5-5.5s5.9 1.9 6.5 5.5" />
      <path d="M15.5 5.2a3.5 3.5 0 0 1 0 6.6M17.8 14.8c2 .7 3.3 2.4 3.7 5.2" />
    </>
  ),
  earnings: (
    <>
      <rect x="2.5" y="6" width="19" height="12.5" rx="3" />
      <circle cx="12" cy="12.25" r="2.75" />
      <path d="M6 9.5v.01M18 15v.01" />
    </>
  ),
  goals: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4.5" />
      <circle cx="12" cy="12" r="0.75" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </>
  ),
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5.5" />
      <circle cx="12" cy="7.75" r="0.5" />
    </>
  ),
  // The booking page's location, cancellation and WhatsApp lines.
  'map-pin': (
    <>
      <path d="M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11z" />
      <circle cx="12" cy="10" r="2.5" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </>
  ),
  message: <path d="M20 11.5a7.5 7.5 0 0 1-11.2 6.5L4 19.5l1.5-4.6A7.5 7.5 0 1 1 20 11.5z" />,
  // A bell, for her notifications.
  bell: (
    <>
      <path d="M6 9a6 6 0 0 1 12 0c0 5 2 7 2.5 8h-17C4 16 6 14 6 9z" />
      <path d="M10 20.5a2 2 0 0 0 4 0" />
    </>
  ),
  more: (
    <>
      <circle cx="5" cy="12" r="1.25" />
      <circle cx="12" cy="12" r="1.25" />
      <circle cx="19" cy="12" r="1.25" />
    </>
  ),
} as const

export type IconName = keyof typeof PATHS

export function Icon({ name, size = 20, label, className, filled = false }: {
  name: IconName
  size?: number
  label?: string
  className?: string
  // Fill the shape instead of stroking it (a chosen star).
  filled?: boolean
}) {
  return (
    <svg
      className={className ? `icon ${className}` : 'icon'}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  )
}

// IconButton: a 44px round target with the icon centred by the grid. Its
// accessible name is `label`, never a glyph.
export const IconButton = React.forwardRef<HTMLButtonElement, {
  icon: IconName
  label: string
  size?: number
  variant?: 'plain' | 'filled' | 'outlined'
  className?: string
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label' | 'children'>>(function IconButton(
  { icon, label, size = 20, variant = 'plain', className, type = 'button', ...rest },
  ref,
) {
  const classes = ['icon-btn', variant !== 'plain' ? `icon-btn--${variant}` : '', className ?? ''].filter(Boolean).join(' ')
  return (
    <button ref={ref} type={type} className={classes} aria-label={label} {...rest}>
      <Icon name={icon} size={size} />
    </button>
  )
})

// BackLink: the one back link (P11), a chevron icon before the place it goes
// back to. Its accessible name is "Back to <place>" unless `label` says
// otherwise, so a screen reader never hears a glyph.
export function BackLink({ children, label, onClick, className }: {
  children: string
  label?: string
  onClick: () => void
  className?: string
}) {
  return (
    <button
      type="button"
      className={className ? `back-link ${className}` : 'back-link'}
      aria-label={label ?? (children === 'Back' ? undefined : `Back to ${children}`)}
      onClick={onClick}
    >
      <Icon name="chevron-left" size={18} />
      {children}
    </button>
  )
}
